import React, { useCallback, useMemo } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import { createDOMID } from '@splunk/ui-utils/id';
import { TabListMenu, useTabListMenu } from '@splunk/dashboard-ui';
import ArrowSquareRight from '@splunk/react-icons/ArrowSquareRight';
import {
    ActionButton,
    type ActionButtonProps,
    useActionButtonClick,
} from '../ActionButton';
import { ActionMenuPopup } from '../ActionMenuPopup';
import type { BaseButtonProps } from '../BaseButton';
import { getButtonTitle } from '../utils/getButtonTitle';

const icon = <ArrowSquareRight />;

export const MoveButton = ({
    itemId,
    dashboardApi,
    screenReaderText,
}: BaseButtonProps): JSX.Element | null => {
    const tabListMenuId = useMemo(() => createDOMID(), []);

    const moveDashboardItems = useCallback(
        (toLayout: string) => {
            // dashboardApi and itemId have to exist to execute this callback
            dashboardApi!.moveDashboardItemsToLayout({
                itemIds: [itemId!],
                toLayoutId: toLayout,
            });
        },
        [dashboardApi, itemId]
    );

    const handleOnClick = useActionButtonClick('move', moveDashboardItems);

    const { open, handleOpen, handleRequestClose, anchor, setRef, tabCount } =
        useTabListMenu();

    const buttonTitle = getButtonTitle({
        base: _('Move'),
        screenReaderText,
    });

    if (!itemId || !dashboardApi || tabCount < 2) {
        return null;
    }

    return (
        <>
            <ActionButton
                data-test="move-button"
                onClick={handleOpen}
                icon={icon}
                elementRef={setRef as ActionButtonProps['elementRef']}
                title={buttonTitle}
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls={tabListMenuId}
            />
            <ActionMenuPopup onRequestClose={handleRequestClose}>
                <TabListMenu
                    open={open}
                    anchor={anchor}
                    onRequestClose={handleRequestClose}
                    onSelectTab={handleOnClick}
                    heading={_('Move to...')}
                    tabListMenuId={tabListMenuId}
                />
            </ActionMenuPopup>
        </>
    );
};
